// Entitlement state derivation shared by the Admin grant/revoke surfaces.
// Mirrors the deployed get_my_entitlement() semantics (canonical RidePulse
// backend): an unexpired premium_override takes precedence, then the
// materialized user_entitlements row — where status='active' with a lapsed
// current_period_end resolves to free/expired, not premium. A badge or local
// flag is never proof of the effective entitlement; callers must feed these
// helpers committed read-back values.

export interface EntitlementFields {
  entitlement_tier?: string | null;
  entitlement_status?: string | null;
  entitlement_platform?: string | null;
  entitlement_period_end?: string | null;
}

const isFuture = (iso: string | null | undefined): boolean =>
  !iso || new Date(iso).getTime() > Date.now();

export const isOverrideActive = (
  override: boolean | null | undefined,
  expiresAt: string | null | undefined,
): boolean => !!override && isFuture(expiresAt);

export const isOverrideExpired = (
  override: boolean | null | undefined,
  expiresAt: string | null | undefined,
): boolean => !!override && !!expiresAt && !isFuture(expiresAt);

export const isPaidActive = (e: EntitlementFields | null | undefined): boolean =>
  !!e && e.entitlement_tier === 'premium' && e.entitlement_status === 'active' && isFuture(e.entitlement_period_end);

export const isPaidExpired = (e: EntitlementFields | null | undefined): boolean =>
  !!e &&
  e.entitlement_tier != null &&
  (e.entitlement_status === 'expired' ||
    (e.entitlement_status === 'active' && !!e.entitlement_period_end && !isFuture(e.entitlement_period_end)));

export type EffectiveEntitlement =
  | { kind: 'admin_grant'; expiresAt: string | null }
  | { kind: 'paid'; platform: string | null; periodEnd: string | null }
  | { kind: 'free' };

export const effectiveEntitlement = (
  override: boolean | null | undefined,
  overrideExpiresAt: string | null | undefined,
  paid: EntitlementFields | null | undefined,
): EffectiveEntitlement => {
  if (isOverrideActive(override, overrideExpiresAt)) {
    return { kind: 'admin_grant', expiresAt: overrideExpiresAt ?? null };
  }
  if (isPaidActive(paid)) {
    return { kind: 'paid', platform: paid!.entitlement_platform ?? null, periodEnd: paid!.entitlement_period_end ?? null };
  }
  return { kind: 'free' };
};

export interface OverrideAndPaidRow extends EntitlementFields {
  premium_override?: boolean | null;
  premium_override_expires_at?: string | null;
  // Present for callers migrating old rows; deliberately NOT used for truth —
  // the deployed flag does not check current_period_end, so it can neither
  // prove Premium nor demote an unknown row to confirmed Free.
  has_active_subscription?: boolean | null;
}

export type PremiumTruth = 'premium' | 'not_premium' | 'unknown';

// Tri-state so absent/malformed entitlement fields surface as "unknown"
// rather than a false Paid/Pro badge or a false confirmed Free.
export const effectivePremiumTruth = (row: OverrideAndPaidRow): PremiumTruth => {
  if (isOverrideActive(row.premium_override, row.premium_override_expires_at)) return 'premium';
  if (row.entitlement_tier === undefined || row.entitlement_status === undefined) return 'unknown';
  return isPaidActive(row) ? 'premium' : 'not_premium';
};

export const platformLabel = (platform: string | null | undefined): string => {
  if (platform === 'apple') return 'Apple';
  if (platform === 'google') return 'Google';
  if (platform === 'both') return 'Apple+Google';
  if (platform === 'admin_override') return 'Admin grant';
  return platform || 'Paid';
};
